import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import NavbarHomeIcon from "@/assets/all-icons/navbar-home-icon.jsx";
import NavbarHomeIconActive from "@/assets/all-icons/navbar-home-icon-active.jsx";
import NavbarNewspaperIcon from "@/assets/all-icons/navbar-newspaper-icon.jsx";
import NavbarSearchIcon from "@/assets/all-icons/navbar-search-icon.jsx";
import NavbarSearchIconActive from "@/assets/all-icons/navbar-search-icon-active.jsx";
import NavbarSuarteworldIcon from "@/assets/all-icons/navbar-suarteworld-icon.jsx";
import UserIcon from "@/assets/all-icons/user-icon.jsx";
import useNotifications from "@/hooks/useNotifications";
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import { getSuarteEnvironment } from '@/utils/environment.utils';

import "./styles/AppNavigationOptions.css";

const LoggedUserIcon = ({
  userSession,
  activeLink,
}) => {
  const { hasNotifications } = useNotifications();

  const { cacheHandler } = useStateHandler();

  const cacheKey = `user_actions.notifications.read`;
  const hasBeenRead = cacheHandler.getCacheValue(cacheKey)?.data;

  const showNewNotifications = hasNotifications && !hasBeenRead;
  const collectorFrameThemeId =
    userSession?.user_flags?.collector_frame_theme ||
    userSession?.user_flags?.collector_frame_theme_id;

  return (
    <span className="app-navigation__nav-icon">
      <div className="app-navigation-profile-image_container">
        <UserProfileImage
          className={`app-navigation__nav-option-profile-image ${activeLink.includes("/profile") ? "active" : ""}`}
          image={userSession.user_image?.image_id}
          typeOfProfile={userSession.user_type}
          userFlags={userSession.user_flags}
          collectorFrameThemeId={collectorFrameThemeId}
        />
        {showNewNotifications && <div className="new-notifications"></div>}
      </div>
    </span>
  );
};

const AppNavigationProtectedOption = ({ activeLink, onLinkClick, optionRef, showNavLabel }) => {
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });
  const navigate = useNavigate();

  const { state } = useStateHandler();

  const handleProfileClick = () => {
    privateActionHandler(() => navigate("/profile"));
  };

  if (state.user_session) {

    return (
      <button
        type="button"
        ref={optionRef}
        className={`app-navigation__nav-option profile ${activeLink.includes("/profile") ? "active" : ""}`}
        aria-label="Profile"
        onClick={() => {
          handleProfileClick();
          onLinkClick("/profile");
        }}
      >
        <LoggedUserIcon
          userSession={state.user_session}
          activeLink={activeLink}
        />
        {showNavLabel && (
          <span className="app-navigation__nav-label">Profile</span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      ref={optionRef}
      className={`app-navigation__nav-option profile ${activeLink === "/profile" ? "active" : ""}`}
      aria-label="Profile"
      onClick={handleProfileClick}
    >
      <span className="app-navigation__nav-icon">
        <UserIcon />
      </span>
      {showNavLabel && (
        <span className="app-navigation__nav-label">Profile</span>
      )}
    </button>
  );
};

const AppNavigationOptions = ({ activeLink, handleLinkClick, onSuarteworldClick }) => {
 
  const navigationItems = useMemo(() => ([
    { key: "home", match: (link) => link === "/" },
    { key: "search", match: (link) => link === "/search" },
    { key: "suarteworld", match: (link) => link === "/suarteworld" },
    { key: "newspaper", match: (link) => link === "/newspaper" },
    { key: "profile", match: (link) => link.includes("/profile") },
  ]), []);

  const navItemsRef = useRef([]);
  const containerRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
  const [suarteEnvironment, setSuarteEnvironment] = useState(() => getSuarteEnvironment());

  const activeIndex = useMemo(() => (
    navigationItems.findIndex((item) => item.match(activeLink))
  ), [activeLink, navigationItems]);

  const updateIndicatorPosition = useCallback(() => {
    const activeElement = navItemsRef.current[activeIndex];
    const containerElement = containerRef.current;

    if (!activeElement || !containerElement) {
      return;
    }

    const { width } = activeElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();
    const left = activeRect.left - containerRect.left;

    setIndicatorStyle((prev) => {
      if (prev.width === width && prev.left === left) {
        return prev;
      }

      return {
        width,
        left,
      };
    });
  }, [activeIndex]);

  useLayoutEffect(() => {
    updateIndicatorPosition();
  }, [updateIndicatorPosition, activeLink]);

  useLayoutEffect(() => {
    const handleResize = () => {
      updateIndicatorPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateIndicatorPosition]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const queries = [
      "(display-mode: standalone)",
      "(display-mode: fullscreen)",
      "(display-mode: minimal-ui)",
    ];
    const listeners = [];

    const handleChange = () => {
      setSuarteEnvironment(getSuarteEnvironment());
    };

    queries.forEach((query) => {
      let mql;
      try {
        mql = window.matchMedia(query);
      } catch (_error) {
        mql = null;
      }

      if (!mql) {
        return;
      }

      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handleChange);
        listeners.push(() => mql.removeEventListener("change", handleChange));
      } else if (typeof mql.addListener === "function") {
        mql.addListener(handleChange);
        listeners.push(() => mql.removeListener(handleChange));
      }
    });

    window.addEventListener("focus", handleChange);
    listeners.push(() => window.removeEventListener("focus", handleChange));

    return () => {
      listeners.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (_error) {
          // no-op
        }
      });
    };
  }, []);

  const suarteOptionsClassName = suarteEnvironment.isBrowser
    ? "app-navigation__suarte-options app-navigation__suarte-options--browser"
    : "app-navigation__suarte-options";

  return (
    <div className={suarteOptionsClassName} ref={containerRef}>
      <NavLink
        to="/"
        ref={(element) => {
          navItemsRef.current[0] = element;
          if (element) {
            updateIndicatorPosition();
          }
        }}
        className={`app-navigation__nav-option home ${activeLink === "/" ? "active" : ""}`}
        aria-label="Home"
        onClick={() => handleLinkClick("/")}
        end
      >
        <span className="app-navigation__nav-icon">
          {activeLink === "/" ? <NavbarHomeIconActive /> : <NavbarHomeIcon />}
        </span>
        {suarteEnvironment.isApp && (
          <span className="app-navigation__nav-label">Home</span>
        )}
      </NavLink>

      <NavLink
        to="/search"
        ref={(element) => {
          navItemsRef.current[1] = element;
          if (element) {
            updateIndicatorPosition();
          }
        }}
        className={`app-navigation__nav-option search ${activeLink === "/search" ? "active" : ""}`}
        aria-label="Search"
        state={location.pathname === "/search" ? location.state : null}
        onClick={() => handleLinkClick("/search")}
      >
        <span className="app-navigation__nav-icon">
          {activeLink === "/search" ? <NavbarSearchIconActive /> : <NavbarSearchIcon />}
        </span>
        {suarteEnvironment.isApp && (
          <span className="app-navigation__nav-label">Search</span>
        )}
      </NavLink>

      <NavLink
        to="/suarteworld"
        ref={(element) => {
          navItemsRef.current[2] = element;
          if (element) {
            updateIndicatorPosition();
          }
        }}
        className={`app-navigation__nav-option suarteworld ${activeLink === "/suarteworld" ? "active" : ""}`}
        aria-label="SuarteWorld"
        state={location.pathname === "/suarteworld" ? location.state : null}
        onClick={(event) => {
          if (onSuarteworldClick) {
            const isModifiedEvent = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

            if (!isModifiedEvent) {
              event.preventDefault();
              onSuarteworldClick();
              return;
            }
          }

          handleLinkClick("/suarteworld");
        }}
      >
        <span className="app-navigation__nav-icon">
          <NavbarSuarteworldIcon />
        </span>
        {suarteEnvironment.isApp && (
          <span className="app-navigation__nav-label">Suarteworld</span>
        )}
      </NavLink>

      <NavLink
        to="/newspaper"
        ref={(element) => {
          navItemsRef.current[3] = element;
          if (element) {
            updateIndicatorPosition();
          }
        }}
        className={`app-navigation__nav-option newspaper ${activeLink === "/newspaper" ? "active" : ""}`}
        aria-label="Newspaper"
        onClick={() => handleLinkClick("/newspaper")}
      >
        <span className="app-navigation__nav-icon">
          <NavbarNewspaperIcon />
        </span>
        {suarteEnvironment.isApp && (
          <span className="app-navigation__nav-label">Newspaper</span>
        )}
      </NavLink>

      <AppNavigationProtectedOption
        activeLink={activeLink}
        onLinkClick={handleLinkClick}
        optionRef={(element) => {
          navItemsRef.current[4] = element;
          if (element) {
            updateIndicatorPosition();
          }
        }}
        showNavLabel={suarteEnvironment.isApp}
      />

      {suarteEnvironment.isApp && (
        <span
          className="app-navigation__active-indicator"
          style={{
            width: indicatorStyle.width,
            transform: `translateX(${indicatorStyle.left}px)`,
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default AppNavigationOptions;
